import { Box, Typography, Stack, Tooltip, useTheme } from "@mui/material";
import { StatusItem } from "api/api-category-status.d";
import { useFetchStatusMasterQuery } from "api/api-category-status";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

interface StatusProgressBarProps {
  category?: string;
  status?: string;
}

type Step = StatusItem;

function filterStepByStatus(status: string, steps: Step[] = []): Step[] {
  const uniqueValue: Record<number, Step> = {};
  steps.forEach((step) => {
    const { sequence, id } = step;
    if (sequence !== null) {
      const key = Math.floor(sequence);
      const isDecimal = sequence % 1 !== 0;
      if (!isDecimal && !uniqueValue[key]) {
        uniqueValue[key] = { ...step, sequence: key };
      } else if (status === id) {
        uniqueValue[key] = { ...step, sequence: key };
      }
    }
  });
  return Object.values(uniqueValue);
}

const STATUS_COLOR_MAP: Record<string, string> = {
  approved: "#43a047", // green
  paid: "#43a047",
  pending: "#fb8c00", // orange
  closed: "#fb8c00",
  rejected: "#e53935", // red
  in_progress: "#1976d2", // blue
  draft: "#757575", // gray
  default: "#1976d2", // fallback blue
};

const getColorByStatus = (status: string) => {
  const key = status?.toLowerCase();
  return STATUS_COLOR_MAP[key] || STATUS_COLOR_MAP.default;
};

const StatusProgressBar: React.FC<StatusProgressBarProps> = ({
  category,
  status,
}) => {
  const { data: statusMaster, isLoading } = useFetchStatusMasterQuery();
  const theme = useTheme();

  if (!category || !status || isLoading || !statusMaster) return null;

  const normalizedCategory = category.trim().toUpperCase();
  const normalizedStatus = status.trim().toUpperCase();

  const rawSteps: Step[] = statusMaster[normalizedCategory] || [];
  const validSteps = rawSteps.filter((step) => step.sequence !== null) as Step[];
  const sortedSteps = validSteps.sort((a, b) => a.sequence! - b.sequence!);
  const filteredSteps = filterStepByStatus(normalizedStatus, sortedSteps);

  const currentStep = filteredSteps.find(
    (step) => step.id.toUpperCase() === normalizedStatus
  );
  if (!currentStep) return null;

  const currentSeq = currentStep.sequence!;
  const isDecimal = currentSeq % 1 !== 0;
  const currentBase = Math.floor(currentSeq);

  const visibleSteps = isDecimal
    ? filteredSteps.filter((step) => Math.floor(step.sequence!) <= currentBase)
    : filteredSteps;

  const currentIndex = visibleSteps.findIndex(
    (step) => step.id.toUpperCase() === normalizedStatus
  );

  const progressPercent =
    currentIndex >= 0 && visibleSteps.length > 1
      ? (currentIndex / (visibleSteps.length - 1)) * 100
      : currentIndex === 0
      ? 0
      : 100;

  // Use the current step's color for the progress fill
  const progressColor = getColorByStatus(currentStep.id);

  return (
    <Box sx={{ width: "100%", maxWidth: 1000, mx: "auto", mt: 4 }} data-component={"StatusProgressBar"}>
      {/* Progress Line with Single-Color Fill */}
      <Box position="relative" width="100%" height={28} mb={1.5}>
        {/* Track */}
        <Box
          position="absolute"
          height={14}
          width="100%"
          top="50%"
          left={0}
          sx={{
            transform: "translateY(-50%)",
            borderRadius: 7,
            background: `linear-gradient(90deg, ${theme.palette.grey[300]} 0%, ${theme.palette.grey[200]} 100%)`,
            boxShadow: `0 2px 16px 0 #bbb2`,
            opacity: 0.25,
          }}
        />
        {/* Progress Fill */}
        <Box
          position="absolute"
          height={14}
          width={`${progressPercent}%`}
          top="50%"
          left={0}
          sx={{
            transform: "translateY(-50%)",
            borderRadius: 7,
            background: progressColor,
            boxShadow: `0 2px 16px 0 ${progressColor}44`,
            transition: "width 0.5s cubic-bezier(.4,2,.6,1)",
            zIndex: 1,
          }}
        />
        {/* Step Circles */}
        <Box
          position="absolute"
          top="50%"
          left={0}
          right={0}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ transform: "translateY(-50%)", px: 2, zIndex: 2 }}
        >
          {visibleSteps.map((step, index) => {
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            const color = getColorByStatus(step.id);
            return (
              <Tooltip
                key={step.id}
                title={step.id.replaceAll("_", " ").toLowerCase()}
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: isCompleted
                      ? color
                      : isCurrent
                      ? "#fff"
                      : theme.palette.grey[200],
                    border: `3px solid ${
                      isCurrent
                        ? color
                        : isCompleted
                        ? color
                        : theme.palette.grey[400]
                    }`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: isCurrent
                      ? `0 0 12px 2px ${color}55`
                      : "none",
                    transition: "all 0.3s cubic-bezier(.4,2,.6,1)",
                    position: "relative",
                    zIndex: 3,
                    animation: isCurrent
                      ? "pulse-progress-bar 1.5s infinite"
                      : "none",
                  }}
                >
                  {isCompleted ? (
                    <CheckCircleIcon sx={{ color: "#fff", fontSize: 22 }} />
                  ) : isCurrent ? (
                    <FiberManualRecordIcon sx={{ color, fontSize: 16 }} />
                  ) : (
                    <RadioButtonUncheckedIcon
                      sx={{ color: color, fontSize: 16 }}
                    />
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
      {/* Step Labels */}
      <Box px={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          {visibleSteps.map((step, index) => {
            const isCurrent = index === currentIndex;
            const color = getColorByStatus(step.id);
            return (
              <Box key={step.id} textAlign="center" sx={{ width: 60, minWidth: 60 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: isCurrent ? "0.95rem" : "0.9rem",
                    color: color,
                    fontWeight: isCurrent ? 900 : 600,
                    textTransform: "capitalize",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textDecoration: isCurrent ? "underline" : "none",
                    letterSpacing: isCurrent ? 0.5 : 0.2,
                  }}
                >
                  {step.id.replaceAll("_", " ").toLowerCase()}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Box>
      <style>
        {`
          @keyframes pulse-progress-bar {
            0% {
              box-shadow: 0 0 0 0 rgba(76,175,80,0.4);
            }
            70% {
              box-shadow: 0 0 0 12px rgba(76,175,80,0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(76,175,80,0);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default StatusProgressBar;
